import React, { useState, useEffect } from 'react'

import { Modal, Box, FormControl, Select, InputLabel, MenuItem, Button } from '@mui/material'
import { useTranslation } from 'react-i18next'
import ClearIcon from "@mui/icons-material/Clear";
import IconButton from "@mui/material/IconButton";
import Swal from 'sweetalert2';

import axios from '../../lib/axios'
import { useStateContext } from '../../context/ContextProvider'
import Loading from '../Loading';
import { handleAxiosError } from '../../helpers/helper';


const AssignForklifter = ({ open, setOpen, orderId, siteId, _forklifter, type = "sales" }) => {

    const { t } = useTranslation()
    const { choosesite, setChoosesite, config } = useStateContext()

    /* The order's own site rather than whichever site is currently selected. The two agree on
     * the daily board, since it only lists orders for the selected site, but the order is the
     * thing a driver has to be co-located with - and siteId comes straight off the row, so it
     * does not depend on the context value having settled. */
    const orderSiteId = siteId ?? choosesite

    const [forklifters, setForklifters] = useState([])
    const [forklifter, setForklifter] = useState('')
    const [isLoading, setIsLoading] = useState(false)

    /* Shared by the option list and the submit button so the two cannot disagree about what is
     * selectable. A driver is only assignable to an order sitting on their own site; the backend
     * enforces the same rule, so submitting an off-site driver just earns a validation error. */
    const isOffSite = (item) => Boolean(orderSiteId) && item?.site_id != orderSiteId

    const selectedForklifter = forklifters.find(item => item.id == forklifter)

    /* Whichever driver the order already carries is pre-selected when the modal opens, so an
     * order that was moved between sites would otherwise open with a live button and fail on
     * submit. An id that matches nothing in the list disables it too - there is nothing to
     * check against, so refusing is safer than sending it. */
    const disabled = !forklifter || isOffSite(selectedForklifter)

    useEffect(() => {
        setChoosesite(localStorage.getItem('site'))
    }, [])

    useEffect(() => {
        if (open) {
            getForklifters()
        }
    }, [open])

    useEffect(() => {
        if (forklifters && forklifters.length > 0) {
            if (_forklifter && _forklifter.id) {
                setForklifter(_forklifter.id)
            }
        }
    }, [forklifters])

    const getForklifters = async () => {
        setIsLoading(true)

        const params = new URLSearchParams({ role: 'forklift_driver', site_id: orderSiteId })

        /* The assigned driver sits on whichever site the order belonged to when they were
         * assigned, so after a site move they no longer match site_id. include_ids keeps them
         * in the response, otherwise the dropdown cannot offer the name it is already showing
         * and re-saving the form would quietly drop the assignment. */
        if (_forklifter && _forklifter.id) {
            params.append('include_ids', _forklifter.id)
        }

        await axios
            .get(`/api/users?${params.toString()}`, config)
            .then(res => {
                const users = res.data.data

                if(users && users.length > 0) {
                    /* Belt and braces: include_ids above should already have returned the
                     * assigned driver, but it cannot if that user has since been deleted. */
                    if (_forklifter && _forklifter.id && !users.some(user => user.id === _forklifter.id)) {
                        setForklifters([...users, _forklifter]);
                        return;
                    }
                    setForklifters(users);
                    return;
                }

                if(_forklifter && _forklifter.id) {
                    setForklifters([_forklifter]);
                } else {
                    setForklifters([]);
                }
            })
            .catch(({ response }) => {
                handleAxiosError({response: response})
                setForklifters(_forklifter && _forklifter.id ? [_forklifter] : [])
            })
            .finally(() => {
                setIsLoading(false)
            })
    }


    const handleToggleModal = () => {
        setOpen(!open)
        setForklifter('')
    }

    const handleClear = () => {
        setForklifter('')
    }

    const assignForklifter = async (e) => {
        e.preventDefault()
        const formData = {}

        formData['forklifter_id'] = forklifter

        await axios.post(`/api/${type}-orders/${orderId}/assign-forklifter`, formData, config).then(({ data }) => {
            Swal.fire({
                icon: "success",
                customClass: 'success',
                showCloseButton: true,
                iconColor: '#00B78E',
                text: data.success.message
            })
            setOpen(false)
        }).catch(({ response }) => {
            if (response.status === 422) {
                Swal.fire({
                    text: response.data.error.message,
                    icon: "error",
                    customClass: 'error',
                    showCloseButton: true,
                    iconColor: '#FF0000'
                })
            } else {
                Swal.fire({
                    text: response.data.error.message,
                    icon: "error",
                    customClass: 'error',
                    showCloseButton: true,
                    iconColor: '#FF0000'
                })
            }
        })
    }



    return (
        <>
        {isLoading ? <Loading position='fixed' /> : ''}
        <Modal open={open} onClose={handleToggleModal}>
            <Box sx={style}>
                <div className='flex justify-between items-center p-5 w-full' style={{ backgroundColor: '#DBA34B', borderRadius: '5px 5px 0 0' }}>
                    <div className='flex items-baseline justify-between w-full'>
                        <p className='text-[18px] font-[600] text-white'>{t('select_forklift_driver')}</p>
                        <div style={{ transform: "rotate(45deg)" }} className="font-[600] text-white">
                            <button onClick={handleToggleModal}><i className="fa-solid fa-plus"></i></button>
                        </div>
                    </div>
                </div>
                <div>
                    <div className='p-5'>
                        <form className='flex justify-center flex-col items-start pt-1 pb-4'>
                            <p className="text-[14px] text-[#718096]">{t('select_forklift_driver')}</p>
                            <Select value={forklifter} onChange={e => { setForklifter(e.target.value) }}
                                sx={{
                                    width: '100%', background: '#FCFCFC', '.MuiOutlinedInput-input': { padding: '9px !important' },
                                    boxShadow: 'none', '.MuiOutlinedInput-notchedOutline': { border: '1px solid #EEEFF2 !important' },
                                    ".MuiSelect-iconOutlined": { display: forklifter ? 'none !important' : '' }
                                }}
                                endAdornment={forklifter ? (<IconButton sx={{ visibility: forklifter ? "visible" : "hidden", padding: '0' }} onClick={handleClear}><ClearIcon /></IconButton>) : false}
                            >
                                {
                                    forklifters.map((item, index) => (
                                        /* A driver returned by include_ids sits on the site the order was moved
                                         * away from, so the name stays readable but cannot be picked again. The
                                         * fallback entry built from _forklifter carries no site_id and is
                                         * disabled for the same reason - it only appears when the driver could
                                         * not be fetched at all. */
                                        <MenuItem
                                            key={index}
                                            value={item.id}
                                            disabled={isOffSite(item)}
                                        >
                                            {item.name}
                                        </MenuItem>
                                    ))
                                }
                            </Select>
                        </form>
                        <div className='w-full flex gap-2 justify-between'>
                            <button type="button" onClick={handleToggleModal} className='w-full text-[14px] text-[#919399] rounded-sm px-4 py-2 bg-[#F3F5F7] hover:bg-[#E6E6E6] disabled:bg-[#B8B7BC] capitalize'>
                                {t('cancel')}
                            </button>
                            <button type="button" onClick={assignForklifter} disabled={disabled} className='w-full text-[14px] text-white rounded-sm px-4 py-2 bg-[#DBA34B] hover:bg-[#CF7300] disabled:bg-[#B8B7BC] capitalize'>
                                {t('select')}
                            </button>
                        </div>
                    </div>
                </div>
            </Box>
        </Modal>
        </>
    )
}

export default AssignForklifter

const style = {
    minWidth: "360px",
    maxWidth: "540px",
    width: "50%",
    height: "auto",
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    bgcolor: '#ffffff',
    border: 'transparent',
    borderRadius: '5px',
    boxShadow: 24,
    zIndex: "1600",
    outline: 'none'
}
